import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { ORIGEM_LABELS, SITUACAO_LABELS, nomeDoMes, type TipoFinanceiro } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import type { LancamentoDaLista } from "@/lib/dinheiro";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { diaParaCelula } from "@/lib/exportar-tabela";
import { cn } from "@/lib/cn";
import { LancamentoDialog } from "./LancamentoDialog";
import { LinhasDeLancamento } from "./LinhasDeLancamento";
import { ResumoDosLancamentos } from "./ResumoDosLancamentos";

type FiltroTipo = "TODOS" | TipoFinanceiro;
type FiltroSituacao = "TODAS" | "ABERTOS" | "PAGO" | "VENCIDO" | "ESTORNADO";

const SITUACOES: { chave: FiltroSituacao; rotulo: string }[] = [
  { chave: "TODAS", rotulo: "Todas" },
  { chave: "ABERTOS", rotulo: "Em aberto" },
  { chave: "VENCIDO", rotulo: "Vencidos" },
  { chave: "PAGO", rotulo: "Pagos" },
  { chave: "ESTORNADO", rotulo: "Estornados" },
];

const pilula = (ativa: boolean) =>
  cn(
    "rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
    ativa
      ? "border-brand-600 bg-brand-50 text-brand-700 dark:border-primary dark:bg-primary/10 dark:text-primary"
      : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted",
  );

export function ListaDeLancamentos({ competencia }: { competencia: string }) {
  const podeLancar = usePermissao("finance.manage");
  const query = trpc.financeiro.lancamentos.listar.useQuery({ competencia });
  const [tipo, setTipo] = useState<FiltroTipo>("TODOS");
  const [situacao, setSituacao] = useState<FiltroSituacao>("TODAS");
  const [dialogo, setDialogo] = useState<{ editando: LancamentoDaLista | null } | null>(null);

  const todos = useMemo(() => query.data ?? [], [query.data]);
  const linhas = useMemo(
    () =>
      todos.filter((l) => {
        if (tipo !== "TODOS" && l.tipo !== tipo) return false;
        if (situacao === "TODAS") return true;
        if (situacao === "ABERTOS") return l.situacao === "PREVISTO" || l.situacao === "VENCIDO";
        if (situacao === "ESTORNADO") return l.situacao === "ESTORNADO" || l.ehEstorno;
        return l.situacao === situacao && !l.ehEstorno;
      }),
    [todos, tipo, situacao],
  );

  return (
    <div className="space-y-4">
      <ResumoDosLancamentos linhas={todos} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {(["TODOS", "ENTRADA", "SAIDA"] as const).map((t) => (
            <button key={t} type="button" onClick={() => setTipo(t)} className={pilula(tipo === t)}>
              {t === "TODOS" ? "Entradas e saídas" : t === "ENTRADA" ? "Entradas" : "Saídas"}
            </button>
          ))}
          <span className="mx-1 hidden h-5 w-px bg-zinc-200 sm:block dark:bg-border" />
          {SITUACOES.map((s) => (
            <button key={s.chave} type="button" onClick={() => setSituacao(s.chave)} className={pilula(situacao === s.chave)}>
              {s.rotulo}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <BotaoExportar
            nome={`lancamentos-${competencia}`}
            titulo="lançamentos"
            planilha="lancamentos"
            tela="lancamentos"
            quantidade={linhas.length}
            idDe={(l: LancamentoDaLista) => l.id}
            buscarLinhas={async () => linhas}
            colunas={[
              { titulo: "Tipo", valor: (l) => (l.tipo === "ENTRADA" ? "Entrada" : "Saída") },
              { titulo: "Descrição", valor: (l) => l.descricao },
              { titulo: "Categoria", valor: (l) => l.categoria.nome },
              { titulo: "Cliente ou fornecedor", valor: (l) => l.partner?.name },
              { titulo: "Valor", valor: (l) => l.valorCents / 100 },
              { titulo: "Vencimento", valor: (l) => diaParaCelula(l.vencimento) },
              { titulo: "Pago em", valor: (l) => diaParaCelula(l.pagoEm) },
              { titulo: "Situação", valor: (l) => (l.ehEstorno ? "Estorno" : SITUACAO_LABELS[l.situacao]) },
              { titulo: "Origem", valor: (l) => ORIGEM_LABELS[l.origem] },
            ]}
          />
          {podeLancar && (
            <button
              type="button"
              onClick={() => setDialogo({ editando: null })}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" /> Novo lançamento
            </button>
          )}
        </div>
      </div>

      {query.isLoading ? (
        <p className="py-10 text-center text-sm text-zinc-500">Carregando…</p>
      ) : linhas.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 py-12 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
          {todos.length === 0 ? `Nenhum lançamento em ${nomeDoMes(competencia)}.` : "Nenhum lançamento com esses filtros."}
        </div>
      ) : (
        <LinhasDeLancamento linhas={linhas} podeLancar={podeLancar} onEditar={(l) => setDialogo({ editando: l })} />
      )}

      <LancamentoDialog aberto={!!dialogo} editando={dialogo?.editando} competencia={competencia} onFechar={() => setDialogo(null)} />
    </div>
  );
}
